import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { getAgentConfigDisplayName } from "../../../utils/render";
import EditLinkDialog from "../assessments/EditLinkDialog";

interface RoleplayLink {
  id: string;
  agent_config_id: string | null;
  created_at: string;
  display_name?: string | null;
  namespace_asset_registry?: {
    namespace_assets: Record<string, string[]>;
  } | null;
  control_graph_key?: string | null;
}

interface RoleplayLinksTableProps {
  roleplayLinks: RoleplayLink[];
  linksLoading: boolean;
  agentConfigs: any[];
  organizationId: string;
  roleplayId: string;
  onCreateLink: (
    agentConfigId: string,
    displayName: string,
    testOverrideHumanDescription: string,
    additionalContext: string,
    controlGraphKey: string | null
  ) => Promise<void>;
  onUpdateLink: (updatedLink: any) => Promise<void>;
}

const RoleplayLinksTable: React.FC<RoleplayLinksTableProps> = ({
  roleplayLinks,
  linksLoading,
  agentConfigs,
  organizationId,
  roleplayId,
  onCreateLink,
  onUpdateLink,
}) => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
  const [selectedAgentConfigId, setSelectedAgentConfigId] = useState("");
  const [linkDisplayName, setLinkDisplayName] = useState("");
  const [linkDisplayNameTouched, setLinkDisplayNameTouched] = useState(false);
  const [testOverrideHumanDescription, setTestOverrideHumanDescription] =
    useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [selectedControlGraphKey, setSelectedControlGraphKey] = useState<
    string | null
  >(null);
  const [editingLink, setEditingLink] = useState<any>(null);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyLinkWithFeedback = (linkId: string, url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const handleCreateLink = async () => {
    if (!selectedAgentConfigId) {
      toast.error("Please select an agent config");
      return;
    }

    try {
      await onCreateLink(
        selectedAgentConfigId,
        linkDisplayName,
        testOverrideHumanDescription,
        additionalContext,
        selectedControlGraphKey
      );
      setCreateLinkDialogOpen(false);
      setSelectedAgentConfigId("");
      setLinkDisplayName("");
      setLinkDisplayNameTouched(false);
      setTestOverrideHumanDescription("");
      setAdditionalContext("");
      setSelectedControlGraphKey(null);
    } catch {
      // Error handling is done in parent
    }
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon color="primary" />
            <Typography variant="h6">Shareable Roleplay Links</Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setCreateLinkDialogOpen(true);
              // Mark as touched immediately since it starts empty
              setLinkDisplayNameTouched(true);
            }}
          >
            Create Link
          </Button>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {linksLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : roleplayLinks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No links have been created for this roleplay yet.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 120,
                      bgcolor: "background.paper",
                    }}
                  >
                    Link ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 150,
                      bgcolor: "background.paper",
                    }}
                  >
                    Link Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 140,
                      bgcolor: "background.paper",
                    }}
                  >
                    Agent Config
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 120,
                      bgcolor: "background.paper",
                    }}
                  >
                    Control Graph
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 80,
                      bgcolor: "background.paper",
                    }}
                  >
                    Created
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 80,
                      bgcolor: "background.paper",
                    }}
                  >
                    Main
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 80,
                      bgcolor: "background.paper",
                    }}
                  >
                    Test
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "0.8rem",
                      p: 0.75,
                      width: 40,
                      bgcolor: "background.paper",
                    }}
                  >
                    {/* Empty header for Edit column */}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roleplayLinks.map((roleplayLink) => {
                  const agentConfig = agentConfigs.find(
                    (config) => config.id === roleplayLink.agent_config_id
                  );
                  const mainLinkUrl = `https://pixels.persona-ai.ai/login?res_link_params_id=${roleplayLink.id}&org_id=${organizationId}`;
                  const testLinkUrl = `https://stagingpixels.persona-ai.ai/login?res_link_params_id=${roleplayLink.id}&org_id=${organizationId}`;
                  return (
                    <TableRow key={roleplayLink.id}>
                      <TableCell sx={{ p: 0.75, maxWidth: 120 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            }}
                            title={roleplayLink.id}
                          >
                            {roleplayLink.id}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyId(roleplayLink.id)}
                            sx={{ p: 0.25, flexShrink: 0 }}
                          >
                            {copiedId === roleplayLink.id ? (
                              <CheckCircleIcon
                                sx={{ fontSize: 14 }}
                                color="success"
                              />
                            ) : (
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ p: 0.75, maxWidth: 150 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={roleplayLink.display_name || "-"}
                        >
                          {roleplayLink.display_name || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: 0.75, maxWidth: 140 }}>
                        {agentConfig && agentConfig.id ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() =>
                              navigate(
                                `/dashboard/agent-configs/${agentConfig.id}`
                              )
                            }
                            sx={{
                              textTransform: "none",
                              p: 0.25,
                              fontSize: "0.75rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              justifyContent: "flex-start",
                              minWidth: 0,
                              display: "block",
                              textAlign: "left",
                              width: "100%",
                              "&:hover": {
                                backgroundColor: "transparent",
                                textDecoration: "underline",
                              },
                            }}
                            title={getAgentConfigDisplayName({
                              display_name: agentConfig.display_name,
                              bot_name: agentConfig.bot_name,
                              id: agentConfig.id,
                            })}
                          >
                            {getAgentConfigDisplayName({
                              display_name: agentConfig.display_name,
                              bot_name: agentConfig.bot_name,
                              id: agentConfig.id,
                            })}
                          </Button>
                        ) : roleplayLink.agent_config_id ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.8rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={roleplayLink.agent_config_id}
                          >
                            {roleplayLink.agent_config_id}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.8rem",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ p: 0.75, maxWidth: 120 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            fontFamily: "monospace",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: roleplayLink.control_graph_key ? "text.primary" : "text.secondary",
                          }}
                          title={roleplayLink.control_graph_key || "Using roleplay default"}
                        >
                          {roleplayLink.control_graph_key || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: 0.75 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                          {new Date(roleplayLink.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "2-digit",
                              day: "2-digit",
                            }
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: 0.75 }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyLinkWithFeedback(
                              `main-${roleplayLink.id}`,
                              mainLinkUrl
                            )
                          }
                          title="Copy main link"
                          color="primary"
                          sx={{ p: 0.5 }}
                        >
                          {copiedLinkId === `main-${roleplayLink.id}` ? (
                            <CheckCircleIcon
                              sx={{ fontSize: 18 }}
                              color="success"
                            />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ p: 0.75 }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyLinkWithFeedback(
                              `test-${roleplayLink.id}`,
                              testLinkUrl
                            )
                          }
                          title="Copy test link"
                          color="secondary"
                          sx={{ p: 0.5 }}
                        >
                          {copiedLinkId === `test-${roleplayLink.id}` ? (
                            <CheckCircleIcon
                              sx={{ fontSize: 18 }}
                              color="success"
                            />
                          ) : (
                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ p: 0.75 }}>
                        <IconButton
                          size="small"
                          onClick={() => setEditingLink(roleplayLink)}
                          title="Edit link"
                          sx={{
                            p: 0.5,
                            transition: "background-color 0.2s",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <EditIcon
                            sx={{
                              fontSize: 18,
                              color: "primary.main",
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Link Dialog */}
      <EditLinkDialog
        open={!!editingLink}
        onClose={() => setEditingLink(null)}
        link={editingLink}
        onSave={async (updatedLink: any) => {
          try {
            await onUpdateLink(updatedLink);
            setEditingLink(null);
          } catch {
            // Error handling is done in parent
          }
        }}
        organizationId={organizationId}
        linkType="roleplay"
      />

      {/* Create Link Dialog */}
      <Dialog
        open={createLinkDialogOpen}
        onClose={() => setCreateLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Roleplay Link</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete
              options={agentConfigs.filter(
                (config) => config.organization_id === organizationId
              )}
              getOptionLabel={(option) =>
                getAgentConfigDisplayName({
                  display_name: option.display_name,
                  bot_name: option.bot_name,
                  id: option.id,
                })
              }
              value={
                agentConfigs.find(
                  (config) => config.id === selectedAgentConfigId
                ) || null
              }
              onChange={(_, newValue) => {
                setSelectedAgentConfigId(newValue?.id || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Agent Config"
                  required
                  helperText="Search by display name, bot name, or ID"
                />
              )}
              fullWidth
            />
            <TextField
              label="Link Display Name"
              value={linkDisplayName}
              onChange={(e) => setLinkDisplayName(e.target.value)}
              onBlur={() => setLinkDisplayNameTouched(true)}
              error={linkDisplayNameTouched && linkDisplayName.trim() === ""}
              helperText={
                linkDisplayNameTouched && linkDisplayName.trim() === ""
                  ? "Link Display Name cannot be empty. A friendly name to identify this link"
                  : "A friendly name to identify this link"
              }
              fullWidth
              required
            />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Test Override Fields (Optional)
            </Typography>
            <TextField
              label="Override Human Description"
              value={testOverrideHumanDescription}
              onChange={(e) => setTestOverrideHumanDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Override the human speaker's description for testing purposes"
            />
            <TextField
              label="Additional Context"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Add additional context for the roleplay"
            />
            <TextField
              fullWidth
              label="Control Graph Key (Optional)"
              value={selectedControlGraphKey || ""}
              onChange={(e) => setSelectedControlGraphKey(e.target.value || null)}
              sx={{ mt: 2 }}
              helperText="Specify a control graph resource key (e.g., 'control_graph:example_key') to override the roleplay's default conversation flow"
              placeholder="control_graph:example_key"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateLinkDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateLink}
            variant="contained"
            disabled={!selectedAgentConfigId || linkDisplayName.trim() === ""}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoleplayLinksTable;
